// The team emblem in the DOM (web /t/<code>, admin). Same geometry and colours as
// `emblemSvg()`; gradient and filter ids come from useId so several can share a page.

import { useId, type CSSProperties } from "react";
import { emblemColors, emblemPath, emblemTextBox, GLOW_BLUR, GLOW_PAD, shapeName, type Emblem } from "./index";

export interface TeamEmblemProps {
  emblem: Emblem;
  /** Size of the shape in px. */
  size: number;
  /** Initials on the emblem (omit below ~40px). */
  initials?: string;
  /** Bloom in the key colour; the SVG grows by GLOW_PAD on each side, pulled back with negative margins. */
  glow?: boolean;
  /** Accessible name; decorative (aria-hidden) when omitted. */
  label?: string;
  className?: string;
  style?: CSSProperties;
}

export function TeamEmblem({ emblem, size, initials, glow = false, label, className, style }: TeamEmblemProps) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const c = emblemColors(emblem.palette);
  const pad = glow ? Math.round(size * GLOW_PAD) : 0;
  const w = size + pad * 2;
  const t = `translate(${pad} ${pad}) scale(${size / 100})`;
  const d = emblemPath(emblem.shape);
  const box = emblemTextBox(emblem.shape);
  return (
    <svg
      className={className}
      width={w}
      height={w}
      viewBox={`0 0 ${w} ${w}`}
      style={{ flex: "0 0 auto", ...(pad ? { margin: -pad } : null), ...style }}
      data-shape={shapeName(emblem.shape)}
      data-palette={emblem.palette}
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
    >
      <defs>
        <radialGradient id={`g${id}`} gradientUnits="userSpaceOnUse" cx="34" cy="26" r="72">
          <stop offset="0" stopColor={c.light} />
          <stop offset="0.62" stopColor={c.key} />
          <stop offset="1" stopColor={c.key} />
        </radialGradient>
        {glow ? (
          <filter id={`b${id}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation={GLOW_BLUR} />
          </filter>
        ) : null}
      </defs>
      {glow ? <path d={d} transform={t} fill={c.key} opacity={0.6} filter={`url(#b${id})`} /> : null}
      <path d={d} transform={t} fill={`url(#g${id})`} />
      {initials ? (
        <text
          x="50"
          y={box.cy}
          transform={t}
          textAnchor="middle"
          dominantBaseline="central"
          fontWeight={700}
          fontSize={box.size}
          fill={c.ink}
          style={{ fontFamily: "inherit" }}
        >
          {initials}
        </text>
      ) : null}
    </svg>
  );
}
